import * as CL from '@design-system/component-library';
import { type JSX, useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import { pushGAEventToDataLayer } from '../../common/analytics.js';
import { dsClass } from '../../common/constants/dsClasses.js';
import { createSupportCaseAuthenticated } from '../../common/fetch.js';
import {
  askAboutInvoiceMsg,
  categoryMsg,
  companyMsg,
  createSupportCaseFromInvoiceMsg,
  descriptionInquiryVisibleInOmaElisaMsg,
  invoiceInqueryMsg,
  invoiceNumberMsg,
  invoiceRelatedClaimMsg,
  messageMsg,
  paymentDetailsMsg,
  refundMsg,
  reporterMsg,
  t,
  writeMessageMsg,
} from '../../common/i18n/index.js';
import { Dropdown } from '../../common/react-hook-form/components/Dropdown.js';
import { TextAreaComponent } from '../../common/react-hook-form/components/TextAreaComponent.js';
import { SiteContext } from '../../public/site/SiteContext.js';
import { DialogWrapper } from '../DialogWrapper/index.js';
import { ModalCloseAndSubmitButtons } from '../Modal/ModalCloseAndSubmitButtons.js';
import type { CommonError } from '../../common/types/errors.js';
import type { PutSupportCaseResponse, SupportCase } from '../../generated/api/models.js';

import './InvoiceSupportCaseDialog.scss';

export interface InvoiceSupportCaseDialogProps {
  featureName: SupportCase.FeatureEnum;
  featureItemId?: string;
  errors?: CommonError[];
  invoiceDisplayId: string;
  creator: string;
  companyName?: string;
  accountMasterId: string;

  onCloseDialog: () => void;
  onSubmitDialog: (response: PutSupportCaseResponse, shoInfoDialog: boolean, noSuccessNotification: boolean) => void;
}

interface InvoiceSupportCaseFormValues {
  subject: string;
  description: string;
}

export const InvoiceSupportCaseDialog = ({
  featureItemId,
  errors,
  invoiceDisplayId,
  creator,
  onCloseDialog,
  onSubmitDialog,
  featureName,
  companyName,
  accountMasterId,
}: InvoiceSupportCaseDialogProps): JSX.Element => {
  const { siteBaseUrl } = useContext(SiteContext);
  const { pathname, search } = useLocation();
  const generalErrors = errors?.filter(error => !error.field) || [];
  const [isSubmitInProgress, setIsSubmitInProgress] = useState(false);

  const methods = useForm<InvoiceSupportCaseFormValues>({
    mode: 'all',
    defaultValues: { subject: '', description: '' },
  });

  useEffect(() => {
    pushGAEventToDataLayer('VIEW_SUPPORT_CASE_MODAL');
  }, [search]);

  const onSubmit = async (values: InvoiceSupportCaseFormValues) => {
    setIsSubmitInProgress(true);
    const res = await createSupportCaseAuthenticated(
      {
        action: t.AM3R(askAboutInvoiceMsg),
        feature: featureName,
        featureItemId: featureItemId,
        url: `${siteBaseUrl}${pathname}${search}`,
        featureType: values.subject,
        description: values.description,
      },
      accountMasterId || ''
    );
    setIsSubmitInProgress(false);
    onSubmitDialog(res, true, true);
  };

  return (
    <DialogWrapper closeable header={t.H4LW(createSupportCaseFromInvoiceMsg)} onCloseDialog={onCloseDialog}>
      <div className={`${dsClass.MARGIN_TOP_4} support-case-dialog`}>
        <div>
          <span className="title">{t.KJTS(companyMsg)}</span>
          <p className={dsClass.MARGIN_TOP_0}>{companyName}</p>
        </div>
        <div>
          <span className="title">{t.VWKR(reporterMsg)}</span>
          <p className={dsClass.MARGIN_TOP_0}>{creator}</p>
        </div>
        <div>
          <span className="title">{t.BRFX(invoiceNumberMsg)}</span>
          <p className={dsClass.MARGIN_TOP_0}>{invoiceDisplayId}</p>
        </div>
        {generalErrors.map((error, i) => (
          <CL.Disclaimer
            className={dsClass.TEXT_M}
            icon={<CL.Icon icon="warning" />}
            key={i}
            text={error.message}
            visible
          />
        ))}

        <FormProvider {...methods}>
          <form noValidate onSubmit={methods.handleSubmit(onSubmit)}>
            <div className={dsClass.MARGIN_TOP_3}>
              <Dropdown
                name="subject"
                label={t.CV5H(categoryMsg)}
                items={[
                  { label: t.EE4N(paymentDetailsMsg), value: t.EE4N(paymentDetailsMsg) },
                  { label: t.AM3R(invoiceInqueryMsg), value: t.AM3R(invoiceInqueryMsg) },
                  { label: t.Z9BX(invoiceRelatedClaimMsg), value: t.Z9BX(invoiceRelatedClaimMsg) },
                  { label: t.R58C(refundMsg), value: t.R58C(refundMsg) },
                ]}
              />
            </div>

            <TextAreaComponent
              name="description"
              label={t.FJLH(messageMsg)}
              placeholder={t.TQLE(writeMessageMsg)}
              required={true}
              disabled={false}
            />

            <span>{t.YAA5(descriptionInquiryVisibleInOmaElisaMsg)}</span>
            <ModalCloseAndSubmitButtons onCancel={onCloseDialog} onSubmit={() => {}} submitting={isSubmitInProgress} />
          </form>
        </FormProvider>
      </div>
    </DialogWrapper>
  );
};
